import type { QueryClient } from "@tanstack/react-query";
import {
  createRootRouteWithContext,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Outlet,
  redirect,
  type RouterHistory,
} from "@tanstack/react-router";
import { RequireAuth } from "@/auth/auth-status";
import { Toaster } from "@/components/ui/sonner";
import { meQueryKey, type Me } from "@/api/me";
import { findWorkspace, firstVisibleSection, PORTAL_SECTIONS } from "@/layout/nav";
import { lazy, useCallback } from "react";
import { PortalLayout } from "@/layout/PortalLayout";
import { StaffShell } from "@/layout/StaffShell";
import { NotFoundInline, NotFoundPage } from "@/pages/NotFoundPage";
import { RouteError } from "@/pages/RouteError";
import { PortalSectionPage } from "@/pages/portal/PortalSectionPage";
import { cleanSearch, validateListSearch, type ListSearch } from "@/entities/list-search";
// Lists and record pages (tables, filters, forms) load on first use, keeping them out of the first download.
const entityScreens = () => import("@/pages/EntityScreens");
const SectionScreen = lazy(() => entityScreens().then((m) => ({ default: m.SectionScreen })));
const SectionRecordScreen = lazy(() => entityScreens().then((m) => ({ default: m.SectionRecordScreen })));
const GenericRecordScreen = lazy(() => entityScreens().then((m) => ({ default: m.GenericRecordScreen })));

export interface RouterContext {
  queryClient: QueryClient;
}

/* ------------------------------------------------------------------ root */
const rootRoute = createRootRouteWithContext<RouterContext>()({
  component: () => (
    <>
      <Outlet />
      <Toaster />
    </>
  ),
  notFoundComponent: NotFoundPage,
});

/* ----------------------------------------------------------- staff shell */
const staffRoute = createRoute({
  getParentRoute: () => rootRoute,
  id: "staff",
  component: () => (
    <RequireAuth>
      <StaffShell />
    </RequireAuth>
  ),
  notFoundComponent: NotFoundInline,
});

const homeRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "/",
  component: lazyRouteComponent(() => import("@/pages/HomePage"), "HomePage"),
});

/** /delivery → /delivery/projects (first section of the workspace the user can see). */
const workspaceRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "$workspace",
  beforeLoad: ({ params, context }) => {
    const ws = findWorkspace(params.workspace);
    const first = ws && firstVisibleSection(ws, context.queryClient.getQueryData<Me>(meQueryKey));
    // Unknown workspace: fall through to the component, which renders the 404.
    // (Throwing notFound() from beforeLoad leaves this router version stuck pending.)
    if (!ws || !first) return;
    throw redirect({
      to: "/$workspace/$section",
      params: { workspace: ws.id, section: first.slug },
      replace: true,
    });
  },
  component: NotFoundInline,
});

/** /delivery/projects: an entity list (metadata-driven) or a "coming soon" page. */
const sectionRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "$workspace/$section",
  validateSearch: (search: Record<string, unknown>): ListSearch => validateListSearch(search),
  component: function SectionRouteComponent() {
    const params = sectionRoute.useParams();
    const search = sectionRoute.useSearch();
    const navigate = sectionRoute.useNavigate();
    const onSearchChange = useCallback(
      (patch: ListSearch) =>
        void navigate({
          to: ".",
          search: (prev: ListSearch) => cleanSearch({ ...prev, ...patch }),
          // Typing in the search box shouldn't flood the history stack.
          replace: "q" in patch,
        }),
      [navigate],
    );
    return (
      <SectionScreen
        key={`${params.workspace}/${params.section}`}
        workspaceId={params.workspace}
        sectionSlug={params.section}
        search={search}
        onSearchChange={onSearchChange}
      />
    );
  },
});

interface RecordSearch {
  tab?: string;
}
// The tab stays in the URL even for Overview: some records open on another tab (an invoice opens on Invoice), so
// leaving it out would send Overview back to that default.
const validateRecordSearch = (search: Record<string, unknown>): RecordSearch =>
  typeof search.tab === "string" ? { tab: search.tab } : {};

/** /support/tickets/{id}: record page for the section's entity. */
const sectionRecordRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "$workspace/$section/$id",
  validateSearch: validateRecordSearch,
  component: function SectionRecordRouteComponent() {
    const params = sectionRecordRoute.useParams();
    const { tab } = sectionRecordRoute.useSearch();
    const navigate = sectionRecordRoute.useNavigate();
    return (
      <SectionRecordScreen
        workspaceId={params.workspace}
        sectionSlug={params.section}
        id={params.id}
        tab={tab}
        onTabChange={(next) =>
          void navigate({ to: ".", search: { tab: next }, replace: true })
        }
      />
    );
  },
});

/** /records/{entity}/{id}: cross-entity lookup links; redirects to the canonical section route when there is one. */
const genericRecordRoute = createRoute({
  getParentRoute: () => staffRoute,
  path: "records/$entity/$id",
  validateSearch: validateRecordSearch,
  component: function GenericRecordRouteComponent() {
    const params = genericRecordRoute.useParams();
    const { tab } = genericRecordRoute.useSearch();
    const navigate = genericRecordRoute.useNavigate();
    return (
      <GenericRecordScreen
        entity={params.entity}
        id={params.id}
        tab={tab}
        onTabChange={(next) =>
          void navigate({ to: ".", search: { tab: next }, replace: true })
        }
      />
    );
  },
});

/* --------------------------------------------------------- client portal */
const portalRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "portal",
  component: () => (
    <RequireAuth>
      <PortalLayout />
    </RequireAuth>
  ),
  notFoundComponent: NotFoundInline,
});

const portalIndexRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "/",
  beforeLoad: () => {
    throw redirect({ to: "/portal/$section", params: { section: "tickets" }, replace: true });
  },
});

const portalSectionRoute = createRoute({
  getParentRoute: () => portalRoute,
  path: "$section",
  component: function PortalSectionRouteComponent() {
    const { section: slug } = portalSectionRoute.useParams();
    const section = PORTAL_SECTIONS.find((s) => s.slug === slug);
    if (!section) return <NotFoundInline />;
    return <PortalSectionPage key={slug} section={section} />;
  },
});

/* ------------------------------------------------------------- dev only */
// Component gallery for design review. Not behind sign-in and stripped from production builds.
const devComponentsRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "dev/components",
  component: import.meta.env.DEV
    ? lazyRouteComponent(() => import("@/pages/dev/ComponentsPage"), "ComponentsPage")
    : NotFoundPage,
});

export const routeTree = rootRoute.addChildren([
  staffRoute.addChildren([homeRoute, workspaceRoute, sectionRoute, sectionRecordRoute, genericRecordRoute]),
  portalRoute.addChildren([portalIndexRoute, portalSectionRoute]),
  devComponentsRoute,
]);

export function createAppRouter(queryClient: QueryClient, history?: RouterHistory) {
  return createRouter({
    routeTree,
    history,
    context: { queryClient },
    defaultPreload: "intent",
    defaultErrorComponent: RouteError,
    scrollRestoration: true,
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
